// Create-team slide-over. Mirrors the Drillcut CreateTeamSlideOver:
// name, monogram, description, private toggle, and a member /
// leads multi-select that picks tenant users via the same PersonSelect
// the rest of the planner uses. Creator is auto-set as a lead by the
// server regardless of what's in the leadUserIds array.
import { useState } from "react";
import { Crown, Plus, Users, X } from "lucide-react";
import { Sheet } from "../components/Sheet";
import { Avatar } from "../components/Avatar";
import { tpApi } from "../api";
import { useResource } from "../hooks";
import { PersonSelect } from "./createSlideOver/selects";
import { DC } from "./createSlideOver/primitives";
import type { PlannerOwner } from "../../../../../../shared/team-planner/schemas";

export function CreateTeamSheet({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [monogram, setMonogram] = useState("");
  const [description, setDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [members, setMembers] = useState<string[]>([]);
  const [leads, setLeads] = useState<string[]>([]);
  const [pickerValue, setPickerValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate display names for the pills. Re-fetched whenever the
  // member list grows so a newly-added user renders their name.
  const profiles = useResource(
    async () => {
      if (members.length === 0) return [] as PlannerOwner[];
      // The search endpoint returns up to 20 rows when q is empty.
      // Pulling all + filtering client-side is fine for the team size
      // we're modelling here (rarely more than 50 members per team).
      const all = await tpApi.searchUsers("");
      return all.filter((u) => members.includes(u.id));
    },
    [members],
  );

  function addPicked() {
    if (!pickerValue) return;
    setMembers((m) => (m.includes(pickerValue) ? m : [...m, pickerValue]));
    setPickerValue(null);
  }
  function toggleLead(userId: string) {
    setLeads((l) => (l.includes(userId) ? l.filter((x) => x !== userId) : [...l, userId]));
  }
  function removeMember(userId: string) {
    setMembers((m) => m.filter((x) => x !== userId));
    setLeads((l) => l.filter((x) => x !== userId));
  }

  async function submit() {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await tpApi.createTeam({
        name: name.trim(),
        monogram: monogram.trim() || undefined,
        description: description.trim() || undefined,
        isPrivate,
        memberUserIds: members,
        leadUserIds: leads,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="New team"
      footer={
        <>
          <button type="button" className="bsp-btn bsp-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            disabled={busy || !name.trim()}
            onClick={submit}
          >
            {busy ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <div className="tp-field">
        <label className="bsp-label">Name</label>
        <input
          className="bsp-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>
      <div className="tp-field-row">
        <div className="tp-field">
          <label className="bsp-label">Monogram</label>
          <input
            className="bsp-input"
            maxLength={4}
            value={monogram}
            onChange={(e) => setMonogram(e.target.value)}
            placeholder="Up to 4 letters"
          />
        </div>
        <div className="tp-field" style={{ justifyContent: "center" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              marginTop: 24,
            }}
          >
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
            />
            Private team
          </label>
        </div>
      </div>
      <div className="tp-field">
        <label className="bsp-label">Description</label>
        <textarea
          className="bsp-input"
          style={{ minHeight: 80, padding: 10, lineHeight: 1.4 }}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="tp-field">
        <label className="bsp-label">
          <Users size={11} style={{ display: "inline", marginRight: 4 }} />
          Members{members.length > 0 ? ` · ${members.length}` : ""}
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
          <PersonSelect value={pickerValue} onChange={setPickerValue} />
          <button
            type="button"
            className="bsp-btn bsp-btn-secondary bsp-btn-sm"
            disabled={!pickerValue}
            onClick={addPicked}
          >
            <Plus size={12} /> Add
          </button>
        </div>
        {members.length > 0 ? (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "10px 0 0",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {members.map((uid) => {
              const profile = profiles.data?.find((p) => p.id === uid);
              const isLead = leads.includes(uid);
              return (
                <li
                  key={uid}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "6px 10px",
                    background: DC.surface,
                    border: `1px solid ${DC.border}`,
                    borderRadius: 4,
                  }}
                >
                  <Avatar
                    owner={{
                      displayName: profile?.displayName ?? null,
                      email: profile?.email ?? null,
                    }}
                  />
                  <span style={{ flex: 1, fontSize: 13 }}>
                    {profile?.displayName ?? uid.slice(0, 8)}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleLead(uid)}
                    title={isLead ? "Demote" : "Promote to lead"}
                    className="tp-icon-action"
                    style={{
                      color: isLead ? "var(--bsp-blue)" : DC.grey,
                      background: isLead ? "var(--bsp-blue-tint)" : "transparent",
                      borderColor: isLead ? "var(--bsp-blue)" : DC.borderStrong,
                    }}
                  >
                    <Crown size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeMember(uid)}
                    title="Remove"
                    className="tp-icon-action"
                  >
                    <X size={12} />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      {error ? <p style={{ color: "var(--bsp-red-fg)", fontSize: 12 }}>{error}</p> : null}
    </Sheet>
  );
}
